import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckboxField } from '@/components/ui/checkbox-field';
import { Field } from '@/components/ui/field';
import { FieldsetGroup } from '@/components/ui/fieldset-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SelectOptionWithDescription } from '@/components/ui/select-option-with-description';
import { Separator } from '@/components/ui/separator';
import {
  DOCUMENT_ACCESS_LEVELS,
  DOCUMENT_CLASSIFICATIONS,
  type DocumentAccessLevel,
  type DocumentClassification,
} from '@constants/documentMetadata';
import { MetadataSection } from './MetadataSection';
import type { MetadataFormData, MetadataPermissions } from './useDocumentMetadataForm';

interface MetadataSecuritySectionProps {
  formData: MetadataFormData;
  onClassificationChange: (value: DocumentClassification) => void;
  onPermissionChange: <K extends keyof MetadataPermissions>(
    key: K,
    value: MetadataPermissions[K],
  ) => void;
}

const MetadataSecuritySection: React.FC<MetadataSecuritySectionProps> = ({
  formData,
  onClassificationChange,
  onPermissionChange,
}) => {
  const { t } = useTranslation();

  return (
    <MetadataSection title={t('metadata.classificationSecurity')}>
      <Field label={t('metadata.classification')}>
        {(control) => (
          <Select
            value={formData.classification}
            onValueChange={(value) => onClassificationChange(value as DocumentClassification)}
          >
            <SelectTrigger {...control}>
              <SelectValue placeholder={t('common.select')} />
            </SelectTrigger>
            <SelectContent className="revy-ui">
              {DOCUMENT_CLASSIFICATIONS.map((option) => (
                <SelectOptionWithDescription
                  key={option.value}
                  value={option.value}
                  name={t(option.nameKey)}
                  description={t(option.descriptionKey)}
                />
              ))}
            </SelectContent>
          </Select>
        )}
      </Field>

      <Separator />

      <Field label={t('metadata.defaultAccessLevel')}>
        {(control) => (
          <Select
            value={formData.permissions.defaultAccess}
            onValueChange={(value) =>
              onPermissionChange('defaultAccess', value as DocumentAccessLevel)
            }
          >
            <SelectTrigger {...control}>
              <SelectValue placeholder={t('common.select')} />
            </SelectTrigger>
            <SelectContent className="revy-ui">
              {DOCUMENT_ACCESS_LEVELS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <span className="revy-cjk">{t(option.labelKey)}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Field>

      {/* No visible heading in the design, so the group borrows documents.permissions. */}
      <FieldsetGroup legend={t('documents.permissions')} hideLegend>
        <CheckboxField
          checked={formData.permissions.allowShare}
          onChange={(checked) => onPermissionChange('allowShare', checked)}
          label={t('metadata.allowShare')}
        />
        <CheckboxField
          checked={formData.permissions.allowDownload}
          onChange={(checked) => onPermissionChange('allowDownload', checked)}
          label={t('metadata.allowDownload')}
        />
      </FieldsetGroup>
    </MetadataSection>
  );
};

export { MetadataSecuritySection };
